import { Controller, useForm } from 'react-hook-form';
import Modal from 'react-modal';
import * as yup from 'yup';

// material ui
import {
  Box,
  Button,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Grid,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

// graphql
import { useCreateCashiersChipsTransferMutation, useCreateCashiersChipsWithdrawalMutation } from '@/generated/graphql';

// interfaces
import type { IManagerCashiersChipsInput } from '@/shared/interfaces';

import { MdOutlinePostAdd } from 'react-icons/md';

interface IUsherProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setRefetchCashiersChipsQuery: React.Dispatch<React.SetStateAction<boolean>>;
  setChipsSummaryRefetch: React.Dispatch<React.SetStateAction<string>>;
}

export const ModalManageCashiersChips = ({ open, setOpen, setRefetchCashiersChipsQuery, setChipsSummaryRefetch }: IUsherProps) => {
  const handleClose = () => setOpen(false);

  const schema = yup
    .object({
      amount: yup.number().required(),
      cashierId: yup.string().required(),
      type: yup.string().required(),
    })
    .required();

  const { reset, handleSubmit, control, formState } = useForm<IManagerCashiersChipsInput>({
    mode: 'onChange',
    defaultValues: {
      amount: '',
      type: 'transfer',
    },
  });

  const [createCashiersChipsTransfer] = useCreateCashiersChipsTransferMutation();
  const [createCashiersChipsWithdrawal] = useCreateCashiersChipsWithdrawalMutation();

  const onSubmit = async (dataInput: IManagerCashiersChipsInput) => {
    const { amount, type } = dataInput;

    const input: IManagerCashiersChipsInput = {
      amount,
      type,
    };

    if (type === 'transfer') {
      const { data } = await createCashiersChipsTransfer({
        variables: { amount: parseInt(amount.toString()) },
      });
      console.log(data, '--');
    } else if (type === 'withdrawal') {
      const { data } = await createCashiersChipsWithdrawal({
        variables: { amount: parseInt(amount.toString()) },
      });
    }

    setChipsSummaryRefetch('cashier chips');
    setRefetchCashiersChipsQuery(true);
    handleClose();
    reset();
  };

  return (
    <>
      <Modal
        isOpen={open}
        onRequestClose={handleClose}
        style={{
          overlay: {
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            color: 'inherit',
            backgroundColor: 'rgba(25, 25, 25, 0.75)',
          },
          content: {
            width: '400px',
            overflow: 'unset',
            position: 'absolute',
            margin: '0.1% auto',
            top: '50%',
            left: '50%',
            right: 'auto',
            bottom: 'auto',
            padding: 0,
            marginRight: '-50%',
            transform: 'translate(-50%, -48vh)',
            border: '0px',
            background: 'transparent',
            WebkitOverflowScrolling: 'touch',
            borderRadius: '4px',
            outline: 'none',
          },
        }}>
        <Box
          sx={{
            backgroundColor: '#fff',
            overflow: 'unset',
            borderRadius: '5px',
          }}
          mt={9}>
          <Box sx={{ px: 5, py: 2 }} alignItems={'center'}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography gutterBottom variant="subtitle2" m={0}>
                Transfer or Withdraw Cashiers Chips
              </Typography>
              <MdOutlinePostAdd fontSize={18} />
            </Stack>
          </Box>
          <Divider />

          <Box px={5} pt={2} pb={2}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <Grid>
                <Controller
                  name="amount"
                  control={control}
                  rules={{
                    required: 'Amount is required',
                    min: { value: 1, message: 'Must be at least 1' },
                    pattern: { value: /^[0-9]*$/, message: 'Must be a number' },
                  }}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      label="Amount"
                      type="text"
                      variant="standard"
                      fullWidth
                      error={!!fieldState.error}
                      helperText={fieldState.error ? fieldState.error.message : null}
                      slotProps={{ inputLabel: { shrink: true } }}
                      sx={{ mt: 5 }}
                    />
                  )}
                />

                <Controller
                  name="type"
                  control={control}
                  rules={{ required: 'Type is required' }}
                  render={({ field, fieldState }) => (
                    <FormControl component="fieldset" error={!!fieldState.error} fullWidth sx={{ mt: 3 }}>
                      <FormLabel component="legend">Type</FormLabel>
                      <RadioGroup
                        {...field} // Spread field props (value, onChange, onBlur, ref)
                        row // Display radio buttons in a row
                        aria-labelledby="preferred-contact-radiogroup-label" // Good for accessibility
                      >
                        <FormControlLabel value="transfer" control={<Radio />} label="Transfer" />
                        <FormControlLabel value="withdrawal" control={<Radio />} label="Withdrawal" />
                      </RadioGroup>
                      {fieldState.error && <FormHelperText>{fieldState.error.message}</FormHelperText>}
                    </FormControl>
                  )}
                />
              </Grid>
              <Grid container justifyContent={'end'} mt={5}>
                <Button
                  type="submit"
                  size="small"
                  variant="contained"
                  sx={{
                    background: 'grey',
                    fontSize: 10,
                    fontWeight: 600,
                    paddingLeft: '20px',
                    paddingRight: '20px',
                    textTransform: 'unset',
                  }}
                  onClick={handleClose}>
                  Cancel
                </Button>
                <Button
                  sx={{
                    ml: 1,
                    fontSize: 10,
                    fontWeight: 600,
                    paddingLeft: '20px',
                    paddingRight: '20px',
                    paddingTop: '8px',
                    paddingBottom: '8px',
                    textTransform: 'unset',
                  }}
                  type="submit"
                  size="small"
                  variant="contained">
                  Create
                </Button>
              </Grid>
            </form>
          </Box>
        </Box>
      </Modal>
    </>
  );
};
